import { Label, Input } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Globe, Building, Users } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { generateWorldContextText, generateEnemyContext, generateItemContext, generateCharacterContext, generateCommunityContext, generateSocietyContext } from '@creanizer/utils'
import type { DungeonInput } from '@creanizer/types'
import { DungeonFormStandard } from './DungeonFormStandard'
import { DungeonFormQuick } from './DungeonFormQuick'
import { DungeonFormDetailed } from './DungeonFormDetailed'
import { DungeonFormBatch } from './DungeonFormBatch'
import { DungeonFormCommon } from './DungeonFormCommon'

interface DungeonFormProps {
  input: DungeonInput
  onChange: (input: DungeonInput) => void
  savedWorlds: any[]
  currentWorld: any
  characters: any[]
  enemies: any[]
  items: any[]
  communities: any[]
  societies: any[]
  selectedWorldId: string
  setSelectedWorldId: (id: string) => void
  selectedCharacterId: string
  setSelectedCharacterId: (id: string) => void
  selectedEnemyId: string
  setSelectedEnemyId: (id: string) => void
  selectedItemId: string
  setSelectedItemId: (id: string) => void
  selectedCommunityId: string
  setSelectedCommunityId: (id: string) => void
  selectedSocietyId: string
  setSelectedSocietyId: (id: string) => void
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
}

export function DungeonForm({ 
  input, 
  onChange,
  savedWorlds,
  currentWorld,
  characters,
  enemies,
  items,
  communities,
  societies,
  selectedWorldId,
  setSelectedWorldId,
  selectedCharacterId,
  setSelectedCharacterId,
  selectedEnemyId,
  setSelectedEnemyId,
  selectedItemId,
  setSelectedItemId,
  selectedCommunityId,
  setSelectedCommunityId,
  selectedSocietyId,
  setSelectedSocietyId,
  useStreaming,
  onStreamingChange
}: DungeonFormProps) {
  const handleModeChange = (value: string) => {
    // デバッグログ（開発環境のみ）
    if (process.env.NODE_ENV === 'development') {
      console.log('[DungeonForm] Mode change:', {
        oldMode: input.mode,
        newMode: value,
        currentFloors: input.floors,
        currentRoomsPerFloor: input.roomsPerFloor
      })
    }
    
    // モード変更時に他のモードの入力をクリア
    const baseFields = {
      mode: value as DungeonInput['mode'],
      // 共通フィールドは保持
      worldContext: input.worldContext,
      communityContext: input.communityContext,
      communityRole: input.communityRole,
      societyContext: input.societyContext,
      societyRole: input.societyRole,
      difficulty: input.difficulty,
      creativityLevel: input.creativityLevel,
      useProMode: input.useProMode,
      additionalRequest: input.additionalRequest,
      // ジェネレータ連携も保持
      relatedEnemyContext: input.relatedEnemyContext,
      enemyRole: input.enemyRole,
      relatedItemContext: input.relatedItemContext,
      itemPlacement: input.itemPlacement,
      relatedCharacterContext: input.relatedCharacterContext,
      characterRole: input.characterRole
    }
    
    if (value === 'standard') {
      // quickとbatch特有のフィールドをクリア
      onChange({
        ...baseFields,
        name: input.name,
        type: input.type,
        theme: input.theme,
        floors: input.floors,
        roomsPerFloor: input.roomsPerFloor,
        layout: input.layout,
        size: input.size,
        lightLevel: input.lightLevel,
        condition: input.condition,
        trapDensity: input.trapDensity,
        enemyDensity: input.enemyDensity,
        treasureRichness: input.treasureRichness,
        puzzleComplexity: input.puzzleComplexity
      })
    } else if (value === 'quick') {
      // standardとbatch特有のフィールドをクリア
      onChange({
        ...baseFields,
        theme: input.theme,
        additionalSettings: input.additionalSettings
      })
    } else if (value === 'detailed') {
      // batchとquick特有のフィールドをクリア、詳細設定は全て保持
      onChange({
        ...baseFields,
        name: input.name,
        type: input.type,
        theme: input.theme,
        floors: input.floors,
        roomsPerFloor: input.roomsPerFloor,
        layout: input.layout,
        size: input.size,
        atmosphere: input.atmosphere,
        environment: input.environment,
        lightLevel: input.lightLevel,
        condition: input.condition,
        trapDensity: input.trapDensity,
        enemyDensity: input.enemyDensity,
        treasureRichness: input.treasureRichness,
        puzzleComplexity: input.puzzleComplexity,
        purpose: input.purpose,
        history: input.history,
        currentInhabitants: input.currentInhabitants,
        secrets: input.secrets,
        additionalSettings: input.additionalSettings
      })
    } else if (value === 'batch') {
      // standardとquick、detailed特有のフィールドをクリア
      onChange({
        ...baseFields,
        numberOfDungeons: input.numberOfDungeons || 3,
        batchTheme: input.batchTheme,
        batchConnection: input.batchConnection,
        additionalSettings: input.additionalSettings
      })
    }
  }
  
  return (
    <div className="space-y-6">
      {/* 世界観選択UI（最優先要素） */}
      {savedWorlds.length > 0 && (
        <div className="mb-4 p-4 border-2 border-primary rounded-lg bg-muted/50">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="h-4 w-4" />
            <Label className="text-base font-semibold">世界観設定（最優先）</Label>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            選択した世界観に完全に適合したダンジョンが生成されます
          </p>
          <Select value={selectedWorldId || 'none'} onValueChange={(value) => {
            if (value === 'none') {
              setSelectedWorldId('')
              onChange({ ...input, worldContext: undefined })
            } else {
              setSelectedWorldId(value)
              const world = savedWorlds.find(w => w.id === value)
              if (world) {
                const worldContext = generateWorldContextText(world.result)
                onChange({ ...input, worldContext })
              }
            }
          }}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="世界観を選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし（汎用的なダンジョン）</SelectItem>
              {savedWorlds.map((world) => (
                <SelectItem key={world.id} value={world.id}>
                  {world.result.name}
                  {world.result.genres && (
                    <span className="text-xs text-muted-foreground ml-2">
                      ({world.result.genres.join(', ')})
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedWorldId && (
            <div className="mt-2 p-2 bg-background rounded text-xs">
              <span className="font-medium">適用中の世界観: </span>
              {savedWorlds.find(w => w.id === selectedWorldId)?.result.name}
            </div>
          )}
        </div>
      )}
      
      {/* コミュニティ連携 */}
      {communities && communities.length > 0 && (
        <div className="mb-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center gap-2 mb-2">
            <Building className="h-4 w-4" />
            <Label className="text-base font-semibold">コミュニティ連携（オプション）</Label>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            特定のコミュニティ（国・都市・村など）と関連したダンジョンを生成します
          </p>
          <Select value={selectedCommunityId || 'none'} onValueChange={(value) => {
            if (value === 'none') {
              setSelectedCommunityId('')
              onChange({ ...input, communityContext: undefined, communityRole: undefined })
            } else {
              setSelectedCommunityId(value)
              const community = communities.find(c => c.id === value)
              if (community) {
                const communityContext = generateCommunityContext(community)
                onChange({ ...input, communityContext })
              }
            }
          }}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="コミュニティを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし</SelectItem>
              {communities.map((community) => (
                <SelectItem key={community.id} value={community.id}>
                  {community.result.basicInfo.name} ({community.result.basicInfo.type})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedCommunityId && selectedCommunityId !== 'none' && (
            <div className="mt-3">
              <Label>ダンジョンとの関係</Label>
              <Input
                className="mt-1"
                placeholder="例：都市の地下に存在、近郊の遺跡、街道沿いの洞窟など"
                value={input.communityRole || ''}
                onChange={(e) => onChange({ ...input, communityRole: e.target.value })}
              />
            </div>
          )}
        </div>
      )}
      
      {/* ソサエティ連携 */}
      {societies && societies.length > 0 && (
        <div className="mb-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4" />
            <Label className="text-base font-semibold">組織（ソサエティ）連携（オプション）</Label>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            特定の組織・集団と関連したダンジョンを生成します
          </p>
          <Select value={selectedSocietyId || 'none'} onValueChange={(value) => {
            if (value === 'none') {
              setSelectedSocietyId('')
              onChange({ ...input, societyContext: undefined, societyRole: undefined })
            } else {
              setSelectedSocietyId(value)
              const society = societies.find(s => s.id === value)
              if (society) {
                const societyContext = generateSocietyContext(society)
                onChange({ ...input, societyContext })
              }
            }
          }}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="組織を選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし</SelectItem>
              {societies.map((society) => (
                <SelectItem key={society.id} value={society.id}>
                  {society.result.basicInfo.name} ({society.result.basicInfo.type})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedSocietyId && selectedSocietyId !== 'none' && (
            <div className="mt-3">
              <Label>ダンジョンとの関係</Label>
              <Input
                className="mt-1"
                placeholder="例：組織の拠点、攻略目標、封印している場所、訓練場など"
                value={input.societyRole || ''}
                onChange={(e) => onChange({ ...input, societyRole: e.target.value })}
              />
            </div>
          )}
        </div>
      )}
      
      {/* 共通フィールド */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">共通設定</h3>
        <DungeonFormCommon
          input={input}
          onChange={onChange}
          useStreaming={useStreaming}
          onStreamingChange={onStreamingChange}
        />
      </div>
      
      {/* モード別フォーム */}
      <Tabs value={input.mode || 'standard'} onValueChange={handleModeChange}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="standard">標準</TabsTrigger>
          <TabsTrigger value="quick">クイック</TabsTrigger>
          <TabsTrigger value="detailed">詳細</TabsTrigger>
          <TabsTrigger value="batch">バッチ</TabsTrigger>
        </TabsList>
        
        <TabsContent value="standard">
          <DungeonFormStandard
            input={input}
            onChange={onChange}
            enemies={enemies}
            items={items}
            characters={characters}
            communities={communities}
            societies={societies}
            selectedEnemyId={selectedEnemyId}
            setSelectedEnemyId={setSelectedEnemyId}
            selectedItemId={selectedItemId}
            setSelectedItemId={setSelectedItemId}
            selectedCharacterId={selectedCharacterId}
            setSelectedCharacterId={setSelectedCharacterId}
            selectedCommunityId={selectedCommunityId}
            setSelectedCommunityId={setSelectedCommunityId}
            selectedSocietyId={selectedSocietyId}
            setSelectedSocietyId={setSelectedSocietyId}
          />
        </TabsContent>
        
        <TabsContent value="quick">
          <DungeonFormQuick input={input} onChange={onChange} />
        </TabsContent>
        
        <TabsContent value="detailed">
          <DungeonFormDetailed
            input={input}
            onChange={onChange}
            enemies={enemies}
            items={items}
            characters={characters}
            selectedEnemyId={selectedEnemyId}
            setSelectedEnemyId={setSelectedEnemyId}
            selectedItemId={selectedItemId}
            setSelectedItemId={setSelectedItemId}
            selectedCharacterId={selectedCharacterId}
            setSelectedCharacterId={setSelectedCharacterId}
          />
        </TabsContent>
        
        <TabsContent value="batch">
          <DungeonFormBatch input={input} onChange={onChange} />
        </TabsContent>
      </Tabs>
    </div>
  )
}